const TermsConditionsForCandidates = () => (
  <div className="flex flex-col gap-2 text-sm font-inter">
    <h2 className="mb-2">Terms & Conditions for Candidates</h2>
    <div className="px-0 md:px-6">
      <p>
        <strong>Effective Date:</strong> May 2, 2025
      </p>
      <p className="mt-4">
        These Terms & Conditions ("Terms") govern your relationship with Qureos
        Inc. ("Qureos," "we," "our," or "us") when you use our platform to
        search for jobs, apply to opportunities, or access career development
        services. These Terms explain your rights and responsibilities as a
        Candidate using the Qureos platform.
      </p>
      <p className="mt-4">
        By creating an account, using the platform, or accessing our services,
        you confirm that you have read, understood, and agree to comply with
        these Terms. We encourage you to read them carefully, as they form a
        binding legal agreement between you and Qureos.
      </p>
    </div>

    <h3 className="mt-8">1. Definitions</h3>
    <div className="px-0 md:px-6">
      <p>
        We start by explaining the key terms that will appear throughout these
        Terms. Understanding these terms is essential to knowing how your use of
        the platform is governed.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>"Candidate"</strong>: An individual who creates a personal
          account on Qureos to apply for jobs, participate in assessments, or
          receive career coaching.
          <p className="mt-2">
            You are the Candidate if you sign up for career-related services on
            our platform.
          </p>
        </li>
        <li className="mb-4">
          <strong>"Platform"</strong>: Refers to the Qureos.com website, mobile
          applications, and associated digital services.
          <p className="mt-2">
            It includes all technologies, tools, and content you access as a
            user.
          </p>
        </li>
        <li className="mb-4">
          <strong>"Services"</strong>: The functionalities Qureos provides to
          Candidates, such as job matching, messaging with employers, skills
          assessments, and learning resources.
          <p className="mt-2">
            Services are the core interactions you engage in while using Qureos.
          </p>
        </li>
        <li className="mb-4">
          <strong>"Employer"</strong>: A business entity that posts jobs and
          interacts with Candidates on the Qureos platform.
          <p className="mt-2">
            Employers rely on the information you provide to evaluate your
            candidacy.
          </p>
        </li>
      </ul>
      <p>
        By clearly defining these terms, we aim to build a foundation of trust
        and transparency from the beginning.
      </p>
    </div>

    <h3 className="mt-8">2. Account Creation and Management</h3>
    <div className="px-0 md:px-6">
      <p>
        Candidates must create an account to access most of Qureos' services.
        This section outlines the requirements for account setup, maintenance,
        and security.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Accurate Information</strong>: You agree to provide true,
          complete, and updated information when registering your account.
          <p className="mt-2">
            Providing false or misleading information may result in suspension
            or termination.
          </p>
        </li>
        <li className="mb-4">
          <strong>Account Security</strong>: You are responsible for maintaining
          the confidentiality of your login credentials.
          <p className="mt-2">
            You must notify Qureos immediately if you suspect unauthorized
            access to your account.
          </p>
        </li>
        <li className="mb-4">
          <strong>Eligibility</strong>: You must be at least 18 years old or
          have reached the age of legal majority in your country to use our
          Services.
          <p className="mt-2">
            By creating an account, you confirm that you meet these eligibility
            criteria.
          </p>
        </li>
      </ul>
      <p>
        Managing your account properly is essential for ensuring the security
        and accuracy of your job applications and profile data.
      </p>
    </div>

    <h3 className="mt-8">3. Use of the Platform</h3>
    <div className="px-0 md:px-6">
      <p>
        We want Candidates to have a meaningful experience on Qureos. This
        section details how you can and cannot use the platform.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Permitted Use</strong>: You may use the platform solely for
          personal career advancement and professional development.
          <p className="mt-2">
            You may not use the platform for unrelated purposes such as
            commercial advertising.
          </p>
        </li>
        <li className="mb-4">
          <strong>Prohibited Activities</strong>: You must not engage in
          fraudulent activities, impersonate others, or post false information.
          <p className="mt-2">
            Violating these rules may result in immediate termination.
          </p>
        </li>
        <li className="mb-4">
          <strong>Content Standards</strong>: Any information you upload (CV,
          video responses, assessments) must be accurate, appropriate, and
          lawful.
          <p className="mt-2">
            Qureos reserves the right to remove content that violates these
            standards.
          </p>
        </li>
      </ul>
      <p>
        Proper use of the platform ensures fairness for all users and preserves
        the integrity of the job search process.
      </p>
    </div>

    <h3 className="mt-8">
      4. Job Applications and Communication with Employers
    </h3>
    <div className="px-0 md:px-6">
      <p>
        Applying to jobs and communicating with Employers are key
        functionalities of Qureos. This section explains your responsibilities
        in these interactions.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Application Accuracy</strong>: Your application materials must
          truthfully represent your experience and qualifications.
          <p className="mt-2">
            Misrepresentation can harm your job prospects and your standing on
            the platform.
          </p>
        </li>
        <li className="mb-4">
          <strong>Respectful Communication</strong>: All communications with
          Employers must be professional, respectful, and lawful.
          <p className="mt-2">
            Abusive, discriminatory, or harassing behavior will not be
            tolerated.
          </p>
        </li>
        <li className="mb-4">
          <strong>Employer Decisions</strong>: Qureos does not control or
          guarantee employment decisions made by Employers.
          <p className="mt-2">
            We provide the platform for interaction but do not influence hiring
            outcomes.
          </p>
        </li>
      </ul>
      <p>
        Engaging thoughtfully and honestly with Employers increases your chances
        of securing meaningful job opportunities.
      </p>
    </div>

    <h3 className="mt-8">5. Assessments and Candidate Recordings</h3>
    <div className="px-0 md:px-6">
      <p>
        Qureos may offer skill assessments, recorded interviews, and career
        coaching exercises as part of the candidate experience.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Voluntary Participation</strong>: Participation in assessments
          and video/audio recordings is voluntary but may enhance your
          visibility to Employers.
          <p className="mt-2">
            Candidates have the choice to skip assessments unless specified by
            the Employer.
          </p>
        </li>
        <li className="mb-4">
          <strong>Use of Recordings</strong>: Recordings are used strictly for
          assessment purposes and are not used for biometric identification.
          <p className="mt-2">
            Assessment recordings are evaluated based on professional criteria.
          </p>
        </li>
        <li className="mb-4">
          <strong>Retention of Assessments</strong>: Assessment materials may be
          retained for a reasonable period to support platform functionality and
          improve services.
          <p className="mt-2">
            You may request deletion of your recordings by contacting Qureos.
          </p>
        </li>
      </ul>
      <p>
        Participating in assessments allows you to showcase your skills and
        gives Employers a more holistic view of your abilities.
      </p>
    </div>

    <h3 className="mt-8">6. Privacy and Data Use</h3>
    <div className="px-0 md:px-6">
      <p>
        Protecting your privacy is critical to Qureos' operations. This section
        summarizes how your personal information is handled on the platform.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Data Collection</strong>: We collect personal data such as
          your name, contact information, career history, and usage activity.
          <p className="mt-2">
            Please review our Privacy Policy for full details.
          </p>
        </li>
        <li className="mb-4">
          <strong>Data Usage</strong>: Your information is used to match you
          with job opportunities, facilitate Employer interactions, and improve
          platform services.
          <p className="mt-2">We do not sell or rent your personal data.</p>
        </li>
        <li className="mb-4">
          <strong>Candidate Rights</strong>: You have rights to access, correct,
          and delete your personal data.
          <p className="mt-2">
            These rights are explained fully in our Privacy Policy.
          </p>
        </li>
      </ul>
      <p>
        We strive to be transparent about how your data is processed and empower
        you to control your personal information.
      </p>
    </div>

    <h3 className="mt-8">7. Termination of Access</h3>
    <div className="px-0 md:px-6">
      <p>
        Qureos reserves the right to suspend or terminate your account if you
        violate these Terms or engage in prohibited activities.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Voluntary Termination</strong>: You may close your account at
          any time through your settings or by contacting support.
          <p className="mt-2">
            Closing your account removes your active profile from Employer
            visibility.
          </p>
        </li>
        <li className="mb-4">
          <strong>Involuntary Termination</strong>: We may suspend or terminate
          accounts involved in fraud, abuse, or other misconduct.
          <p className="mt-2">
            Repeated violations will result in permanent removal.
          </p>
        </li>
        <li className="mb-4">
          <strong>Effect of Termination</strong>: Upon termination, you lose
          access to all platform services, including saved applications and
          assessments.
          <p className="mt-2">
            Data deletion policies apply as described in our Privacy Policy.
          </p>
        </li>
      </ul>
      <p>
        Maintaining a positive and respectful presence ensures continued access
        to career opportunities.
      </p>
    </div>

    <h3 className="mt-8">8. Intellectual Property</h3>
    <div className="px-0 md:px-6">
      <p>
        Qureos retains all rights to the platform's design, features, branding,
        and content.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>License to Use</strong>: We grant you a limited, non-exclusive
          license to access and use Qureos solely for your personal job search.
          <p className="mt-2">
            You may not copy, modify, or distribute platform content without
            permission.
          </p>
        </li>
        <li className="mb-4">
          <strong>Candidate Content</strong>: By uploading materials (e.g.,
          resumes, videos), you grant Qureos the right to display and share this
          content for employment purposes.
          <p className="mt-2">
            This license ends when you delete your account or specific content.
          </p>
        </li>
        <li className="mb-4">
          <strong>No Unauthorized Reuse</strong>: Unauthorized scraping, resale,
          or distribution of Qureos content is prohibited.
          <p className="mt-2">Legal action may be taken for violations.</p>
        </li>
      </ul>
      <p>
        Respecting intellectual property helps us maintain a fair and innovative
        ecosystem for all users.
      </p>
    </div>

    <h3 className="mt-8">9. Limitation of Liability</h3>
    <div className="px-0 md:px-6">
      <p>
        While we work hard to ensure a seamless experience, certain limitations
        on our responsibilities apply.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>No Guarantee of Employment</strong>: Qureos does not guarantee
          that using our platform will result in job offers or interviews.
        </li>
        <li className="mb-4">
          <strong>Service Availability</strong>: We strive for consistent
          platform uptime but cannot guarantee uninterrupted access.
        </li>
        <li className="mb-4">
          <strong>Limited Liability</strong>: Our total liability is limited to
          the amount paid by you (if any) in the preceding 12 months.
        </li>
      </ul>
      <p>
        By setting reasonable expectations, we can continue to provide valuable
        career resources to a global audience.
      </p>
    </div>

    <h3 className="mt-8">10. Changes to These Terms</h3>
    <div className="px-0 md:px-6">
      <p>
        We may update these Terms periodically to reflect changes in our
        services, technology, or legal obligations.
      </p>
      <ul className="list-disc pl-5">
        <li className="mb-4">
          <strong>Notification</strong>: Material changes will be communicated
          via email or platform notifications.
        </li>
        <li className="mb-4">
          <strong>Acceptance</strong>: Continued use of the platform after
          changes constitutes acceptance of the updated Terms.
        </li>
        <li className="mb-4">
          <strong>Review</strong>: We recommend reviewing these Terms regularly
          to stay informed.
        </li>
      </ul>
      <p>
        Your awareness and acceptance of updates help us maintain a transparent
        and evolving platform relationship.
      </p>
    </div>

    <h3 className="mt-8">11. Contact Us</h3>
    <div className="px-0 md:px-6">
      <p>
        If you have any questions, feedback, or concerns regarding these Terms
        or your use of the platform, please reach out:
      </p>
      <p>
        <strong>Qureos Inc.</strong>
      </p>
      <ul className="list-disc pl-5">
        <li>
          Email:{' '}
          <a href="mailto:hello@qureos.com" className="text-qureosPrimary">
            hello@qureos.com
          </a>
        </li>
        <li>Jurisdiction: UAE and KSA</li>
      </ul>
    </div>
  </div>
)

export default TermsConditionsForCandidates
